import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle,
  Trophy,
  RotateCcw,
  ChevronRight,
  Download
} from "lucide-react";

export default function QuizPage() {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const [sop, setSop] = useState(null);
  const [progress, setProgress] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [sopId]);

  const fetchData = async () => {
    try {
      const [sopRes, progressRes] = await Promise.all([
        apiClient.get(`/sops/${sopId}`),
        apiClient.get(`/progress/${sopId}`),
      ]);
      setSop(sopRes.data);
      setProgress(progressRes.data);

      if (!sopRes.data.quiz_questions?.length) {
        toast.error("No assessment available for this module");
        navigate(`/sops/${sopId}`);
      }
    } catch (error) {
      console.error("Error fetching quiz:", error);
      toast.error("Failed to load assessment");
      navigate("/sops");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    if (showResult) return; // Prevent duplicate submissions

    const isCorrect = selectedAnswer === sop.quiz_questions[currentQuestion].correct_index;
    setAnswers([...answers, { selected: selectedAnswer, correct: isCorrect }]);
    setShowResult(true);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < sop.quiz_questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      completeQuiz();
    }
  };

  const completeQuiz = async () => {
    // The final answer was already added in handleSubmitAnswer, so use the current answers array
    // Note: Since React state updates are async, we need to use the answers state directly
    // which already contains all answers including the last one
    const totalQuestions = sop.quiz_questions.length;
    
    // answers array already has the last answer from handleSubmitAnswer
    const correctAnswers = answers.filter((a) => a.correct).length;
    const score = Math.round((correctAnswers / totalQuestions) * 100);
    const passed = score >= 80;

    try {
      const allSteps = sop.steps.map((_, index) => index);
      await apiClient.put(`/progress/${sopId}`, {
        sop_id: sopId,
        completed_steps: allSteps,
        quiz_score: score,
        quiz_passed: passed,
        completed: passed,
      });

      setQuizCompleted(true);
      if (passed) {
        toast.success("Congratulations! You've passed the assessment!");
      } else {
        toast.error("You need 80% to pass. Review the material and try again.");
      }
    } catch (error) {
      console.error("Error saving quiz results:", error);
      toast.error("Failed to save results");
    }
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setAnswers([]);
    setShowResult(false);
    setQuizCompleted(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="skeleton h-8 w-32"></div>
          <div className="skeleton h-64 w-full"></div>
        </div>
      </Layout>
    );
  }

  if (!sop) return null;

  // Calculate correct count only from saved answers (already includes all submitted answers)
  const correctCount = answers.filter((a) => a.correct).length;
  const totalQuestions = sop.quiz_questions.length;
  const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

  if (quizCompleted) {
    const passed = score >= 80;

    return (
      <Layout>
        <div className="max-w-2xl mx-auto animate-fade-in-up">
          <Card className="border-[hsl(var(--border))] overflow-hidden">
            <div className={`h-2 ${passed ? "bg-[var(--rd-gold)]" : "bg-red-500"}`} />
            <CardContent className="p-8 text-center">
              <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
                passed ? "bg-[var(--rd-gold)]" : "bg-red-100"
              }`}>
                {passed ? (
                  <Trophy className="w-12 h-12 text-white" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-500" />
                )}
              </div>

              <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                {passed ? "Congratulations!" : "Keep Practising"}
              </h1>
              <p className="text-[hsl(var(--muted-foreground))] mb-8">
                {passed
                  ? "You have successfully completed this training module."
                  : "You need 80% to pass. Review the material and try again."}
              </p>

              <div className="bg-[hsl(var(--surface))] rounded-sm p-6 mb-8 border border-[hsl(var(--border))]">
                <div className="text-5xl font-bold text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {score}%
                </div>
                <p className="text-[hsl(var(--muted-foreground))]">
                  {correctCount} of {totalQuestions} correct
                </p>
              </div>

              {passed && (
                <div className="certificate-frame mb-8">
                  <div className="text-center">
                    <img
                      src="https://customer-assets.emergentagent.com/job_clubhouse-standards/artifacts/tsx8cmf7_CMYK_RD%20small.jpg"
                      alt="Royal Dornoch"
                      className="w-16 h-16 object-contain mx-auto mb-4"
                    />
                    <p className="text-xs font-semibold tracking-wider uppercase text-[var(--rd-gold)] mb-3">
                      Certificate of Completion
                    </p>
                    <h2 className="text-xl text-[var(--rd-green)] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                      {sop.title}
                    </h2>
                    <p className="text-[hsl(var(--muted-foreground))] text-sm">
                      Royal Dornoch Standard Achieved
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-4 justify-center flex-wrap">
                {passed && (
                  <Button
                    onClick={() => {
                      const token = localStorage.getItem("token");
                      const backendUrl = process.env.REACT_APP_BACKEND_URL;
                      fetch(`${backendUrl}/api/certificate/${sopId}/download`, {
                        headers: { "Authorization": `Bearer ${token}` }
                      })
                        .then(res => res.blob())
                        .then(blob => {
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `RoyalDornoch_Certificate_${sop.title.replace(/\s+/g, '_')}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          a.remove();
                        })
                        .catch(() => toast.error("Failed to download certificate"));
                    }}
                    variant="outline"
                    className="border-[var(--rd-gold)] text-[var(--rd-gold)] hover:bg-[var(--rd-gold)] hover:text-white"
                    data-testid="download-certificate-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Certificate
                  </Button>
                )}
                {!passed && (
                  <Button
                    onClick={handleRetakeQuiz}
                    variant="outline"
                    className="border-[var(--rd-green)] text-[var(--rd-green)] hover:bg-[var(--rd-green)] hover:text-white"
                    data-testid="retake-quiz-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Retake Assessment
                  </Button>
                )}
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white"
                  data-testid="back-to-dashboard-btn"
                >
                  Back to Dashboard
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const question = sop.quiz_questions[currentQuestion];

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in-up">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/sops/${sopId}`)}
          className="text-[hsl(var(--muted-foreground))] hover:text-[var(--rd-green)] -ml-2"
          data-testid="back-to-sop-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Module
        </Button>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm text-[hsl(var(--muted-foreground))] mb-2">
            <span>Question {currentQuestion + 1} of {totalQuestions}</span>
            <span>{Math.round(((currentQuestion) / totalQuestions) * 100)}% complete</span>
          </div>
          <div className="rd-progress-track">
            <div 
              className="rd-progress-fill" 
              style={{ width: `${((currentQuestion + 1) / totalQuestions) * 100}%` }} 
            />
          </div>
        </div>

        {/* Question Card */}
        <Card className="border-[hsl(var(--border))]">
          <CardContent className="p-6 lg:p-8">
            <p className="text-xs font-semibold tracking-wider uppercase text-[var(--rd-gold)] mb-4">
              {sop.title}
            </p>
            <h2 className="text-lg lg:text-xl text-[var(--rd-green)] mb-8" style={{ fontFamily: "'Cinzel', serif" }}>
              {question.question}
            </h2>

            <div className="space-y-3">
              {question.options.map((option, index) => {
                let className = "quiz-option";
                if (showResult) {
                  if (index === question.correct_index) {
                    className += " correct";
                  } else if (index === selectedAnswer && index !== question.correct_index) {
                    className += " incorrect";
                  }
                } else if (index === selectedAnswer) {
                  className += " selected";
                }

                return (
                  <div
                    key={index}
                    className={className}
                    onClick={() => handleAnswerSelect(index)}
                    data-testid={`quiz-option-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        showResult && index === question.correct_index
                          ? "border-green-500 bg-green-500 text-white"
                          : showResult && index === selectedAnswer && index !== question.correct_index
                          ? "border-red-500 bg-red-500 text-white"
                          : selectedAnswer === index
                          ? "border-[var(--rd-green)] bg-[var(--rd-green)] text-white"
                          : "border-[hsl(var(--border))]"
                      }`}>
                        {showResult && index === question.correct_index ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : showResult && index === selectedAnswer && index !== question.correct_index ? (
                          <XCircle className="w-4 h-4" />
                        ) : (
                          <span className="text-sm font-medium">{String.fromCharCode(65 + index)}</span>
                        )}
                      </div>
                      <span className="text-[hsl(var(--foreground))]">{option}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          {!showResult ? (
            <Button
              onClick={handleSubmitAnswer}
              disabled={selectedAnswer === null}
              className="bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white disabled:opacity-50"
              data-testid="submit-answer-btn"
            >
              Submit Answer
            </Button>
          ) : (
            <Button
              onClick={handleNextQuestion}
              className="bg-[var(--rd-green)] hover:bg-[var(--rd-green-dark)] text-white"
              data-testid="next-question-btn"
            >
              {currentQuestion < totalQuestions - 1 ? "Next Question" : "See Results"}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
}
